import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LogIn, Loader2, Sparkles, Shield, KeyRound } from 'lucide-react'
import { useAuth } from './AuthContext'
import { emitDevDiagnostic } from '../../lib/devDiagnostics'
import { BuildBadge } from '../BuildBadge'

/** Available auth methods discovered from the gateway (ADR-0025 Decision 9). */
interface AuthMethods {
  local: boolean
  microsoft: boolean
  cac: boolean
}

/**
 * Gateway base URL for full-page redirect flows (OIDC, OAuth).
 *
 * API calls use relative paths (proxied by Vite in dev), but OIDC is a
 * full-page redirect chain that must target the gateway directly so the
 * browser stays on the gateway's origin throughout the flow. The gateway
 * then redirects back to the SPA origin after authentication completes.
 */
const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || ''

/**
 * Login page — dark glass aesthetic.
 *
 * Design: Tufte-inspired — no decorative noise, generous whitespace,
 * clear visual hierarchy. The card is the only element that demands
 * attention. Background is quiet.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { refetch } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [authMethods, setAuthMethods] = useState<AuthMethods>({
    local: true,
    microsoft: true,
    cac: false,
  })

  // Discover available auth methods from the gateway (ADR-0025 Decision 9)
  useEffect(() => {
    fetch('/api/auth/methods')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: AuthMethods | null) => {
        if (data) setAuthMethods(data)
      })
      .catch((err: Error) => {
        emitDevDiagnostic('warn', 'auth_methods_fetch_failed', { error: err.message })
      })
  }, [])

  useEffect(() => {
    const oauthError = searchParams.get('error')
    if (oauthError) {
      setError(decodeURIComponent(oauthError))
    }
  }, [searchParams])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.message ?? 'Login failed')
      }

      await refetch()
      navigate('/')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-[#0a0f1e]">
      {/* Quiet vignette — replaces the noisy grid + glow orbs */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(59, 130, 246, 0.06) 0%, transparent 60%)',
        }}
      />

      {/* Card */}
      <div
        className="relative z-10 w-full mx-4"
        style={{
          maxWidth: '26rem',
          backgroundColor: 'rgba(15, 23, 42, 0.65)',
          border: '1px solid rgba(148, 163, 184, 0.08)',
          borderRadius: '1rem',
          boxShadow: '0 32px 64px -16px rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(24px)',
        }}
      >
        {/* Header */}
        <div className="px-10 pt-10 pb-0 text-center">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto bg-gradient-accent shadow-[0_6px_20px_rgba(59,130,246,0.25)]">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="mt-5 text-xl font-semibold tracking-tight text-white">
            DefconAI Platform
          </h1>
          <p className="mt-1.5 text-[13px] text-slate-500 leading-relaxed">
            Mission-grade analytics, production-grade software
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-10 pt-8 pb-0">
          {error && (
            <div className="mb-5 px-3.5 py-2.5 rounded-lg text-[13px] leading-snug bg-error/8 border border-error/15 text-error">
              {error}
            </div>
          )}

          <fieldset disabled={isLoading} className="space-y-5">
            {/* Username */}
            <div>
              <label
                htmlFor="email"
                className="block text-[11px] font-medium uppercase tracking-wider text-slate-500 mb-2"
              >
                Username
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@defconai.com"
                className="w-full h-11 px-3.5 rounded-lg text-[14px] text-slate-100 placeholder:text-slate-600 bg-white/[0.04] border border-white/[0.08] outline-none transition-colors focus:border-accent/60 focus:bg-white/[0.06]"
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="password"
                  className="block text-[11px] font-medium uppercase tracking-wider text-slate-500"
                >
                  Password
                </label>
                <button
                  type="button"
                  className="text-[11px] font-medium text-accent/70 hover:text-accent bg-transparent border-none cursor-pointer transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="w-full h-11 px-3.5 rounded-lg text-[14px] text-slate-100 placeholder:text-slate-600 bg-white/[0.04] border border-white/[0.08] outline-none transition-colors focus:border-accent/60 focus:bg-white/[0.06]"
              />
            </div>

            {/* Submit */}
            <div className="pt-1">
              <button
                type="submit"
                className="w-full h-11 rounded-lg text-[14px] font-semibold text-white transition-all cursor-pointer flex items-center justify-center gap-2 border-none disabled:opacity-60"
                style={{
                  background:
                    'linear-gradient(135deg, var(--accent-gradient-from), var(--accent-gradient-to))',
                }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin h-4 w-4" />
                    Signing in…
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" />
                    Sign In
                  </>
                )}
              </button>
            </div>
          </fieldset>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/[0.06]" />
            </div>
            <div className="relative flex justify-center">
              <span
                className="px-3 text-[11px] uppercase tracking-wider text-slate-600"
                style={{ backgroundColor: 'rgba(15, 23, 42, 0.65)' }}
              >
                or
              </span>
            </div>
          </div>

          {/* CAC/PIV login (ADR-0025 Decision 9) — shown only when gateway reports cac:true */}
          {authMethods.cac && (
            <button
              type="button"
              disabled={isLoading}
              className="w-full h-11 rounded-lg text-[13px] font-medium transition-all cursor-pointer flex items-center justify-center gap-2.5 bg-emerald-500/[0.06] border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/[0.10] hover:border-emerald-500/30 hover:text-emerald-300 disabled:opacity-60 mb-3"
              onClick={() => {
                const returnUrl = encodeURIComponent(window.location.origin + '/')
                window.location.href = `${GATEWAY_URL}/challenge/CAC?returnUrl=${returnUrl}`
              }}
            >
              <KeyRound className="h-4 w-4" />
              Sign in with PIV/CAC
            </button>
          )}

          {/* Microsoft login */}
          {authMethods.microsoft && (
            <button
              type="button"
              disabled={isLoading}
              className="w-full h-11 rounded-lg text-[13px] font-medium transition-all cursor-pointer flex items-center justify-center gap-2.5 bg-white/[0.03] border border-white/[0.08] text-slate-400 hover:bg-white/[0.06] hover:border-white/[0.12] hover:text-slate-300 disabled:opacity-60"
              onClick={async () => {
                setError(null)
                setIsLoading(true)
                try {
                  const returnUrl = encodeURIComponent(window.location.origin + '/')
                  const challengeUrl = `${GATEWAY_URL}/challenge/Microsoft?returnUrl=${returnUrl}`
                  const res = await fetch(challengeUrl, { redirect: 'manual' })

                  if (res.type === 'opaqueredirect' || res.status === 302) {
                    window.location.href = challengeUrl
                  } else if (res.status === 501) {
                    const data = await res.json().catch(() => ({}))
                    setError(
                      data.message ?? 'Microsoft login is not configured for this environment.',
                    )
                  } else {
                    setError('Microsoft login is unavailable. Contact your administrator.')
                  }
                } catch {
                  setError('Cannot reach the authentication server. Is the gateway running?')
                } finally {
                  setIsLoading(false)
                }
              }}
            >
              <svg
                className="h-4 w-4"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
              </svg>
              Sign in with Microsoft
            </button>
          )}
        </form>

        {/* Footer — quiet, recedes */}
        <div className="px-10 py-6 mt-6 border-t border-white/[0.04]">
          <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-600/60">
            <Shield className="h-3 w-3" />
            <span>Protected by AppSecurity</span>
          </div>
        </div>
      </div>

      {/* Page footer */}
      <div className="relative z-10 mt-8 flex flex-col items-center gap-2 text-[11px] tracking-wide text-slate-400">
        <span>DefconAI Platform v1.0</span>
        <BuildBadge className="border-white/10 bg-white/[0.04] px-2 py-0.5 text-slate-400" />
      </div>
    </div>
  )
}
